import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useDeleteOrgGHGroupMutation,
  useModifyOrgGHGroupMutation,
} from "../../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../../api/network/errorCodes";
import {
  setSelectedGroupIsModified,
  setModalsData,
  TModalsState,
} from "../../../../api/store/commonSlice";
import svgIcons from "../../../../assets/svg/svgIcons";
import { AdminFormFieldRadio } from "../../../../components/admin/formFields";
import { useHGSelectorContext } from "../../../../components/common/hierarchyGroupSelector.context";
import AppDropdownMenu from "../../../../components/dropdownMenu";
import { ACL_NAMES, useACL } from "../../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../../admin-details/role/constants";
import { useGHContext } from "../contexts/groupHierarchy.context";
import ModalDeleteConfirmation from "../../../../components/admin/modalDeleteConfirmation";

type TGroupHierarchyListItemProps = {
  assetsCount?: number;
  group_id: string;
  description: string;
  index: number;
  level: number;
  onDropdownOpen?: () => void;
  parent_group_id: string | null;
  selected?: boolean;
  title: string;
};

const GroupHierarchyListItem: FC<TGroupHierarchyListItemProps> = ({
  assetsCount = 0,
  group_id,
  description,
  index,
  level,
  onDropdownOpen,
  parent_group_id,
  selected = false,
  title,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.groups.hierarchy",
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const [showDropdown, setShowDropdown] = useState(false);
  const [markedForDeletion, setMarkedForDeletion] = useState<string | null>(
    null
  );

  const {
    hierarchyOnDisplay,
    setHierarchyOnDisplay,
    movingGroup,
    setMovingGroup,
    parentOfMovingGroup,
    setParentOfMovingGroup,
    moveToGroup,
    setMoveToGroup,
  } = useGHContext();

  const handleGroupClick = () => {
    setHierarchyOnDisplay([
      // hierarchy before the current level
      ...hierarchyOnDisplay.slice(0, level),
      // current level
      {
        ...hierarchyOnDisplay[level],
        selectedGroup: group_id,
      },
      // hierarchy after the current level, i.e. newly opened level
      {
        group_id,
        level: level + 1,
        groups: [],
        selectedGroup: null,
        lastPage: 1,
      },
    ]);
  };

  const [deleteOrgGrpAPITrigger, { isLoading: isLoadingDeleteOrgGrp }] =
    useDeleteOrgGHGroupMutation();

  const handleClickMoreVertIcon = () => {
    if (!showDropdown) {
      onDropdownOpen && onDropdownOpen();
    }
    setShowDropdown(!showDropdown);
  };

  const groupBeingMovedCanMoveHere =
    // movingGroup is not false
    !!movingGroup &&
    // movingGroup is not the current group, or
    movingGroup !== group_id &&
    // movingGroup is parent of the group being moved
    parentOfMovingGroup !== group_id;

  const { hGSelectorMode, selectedHGroups, setSelectedHGroups } =
    useHGSelectorContext();

  const [modifyOrgGHGroupAPITrigger, { isLoading }] =
    useModifyOrgGHGroupMutation();

  const handleActionEdit = () => {
    setShowDropdown(false);
    dispatch(
      setModalsData({
        ...modalsState,
        editGroupId: group_id,
        showEditGroup: true,
      })
    );
  };

  const handleActionMove = () => {
    setMovingGroup(group_id);
    setParentOfMovingGroup(parent_group_id);
    setShowDropdown(false);
    // close hierarchy after the current level
    setHierarchyOnDisplay(hierarchyOnDisplay.slice(0, level + 1));
  };

  const handleActionMakeParent = () => {
    setShowDropdown(false);
    // make the current group a parent group
    modifyOrgGHGroupAPITrigger({
      parent_group: "",
      groupid: group_id,
    })
      .then(() => {
        toast.success(t("toast.move_success"));
      })
      .catch((error) => {
        toast.error(serializeErrorKeyValues(error));
      });
  };

  const handleActionDelete = () => {
    setShowDropdown(false);
    dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true }));
  };

  const handleDeleteConfirm = () => {
    deleteOrgGrpAPITrigger({ groupid: group_id })
      .unwrap()
      .then(() => {
        toast.success(t("toast.group_deleted"));
        // more operations here after group deletion
        // ------------
      })
      .catch((error: any) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
        // more operations here after group deletion failed
        // ------------
      })
      .finally(() => {
        setMarkedForDeletion(null);
        setShowDropdown(false);
      });
  };

  return (
    <>
      <div className="relative h-10">
        {/* Radio button for moving group */}
        {groupBeingMovedCanMoveHere && (
          <AdminFormFieldRadio
            customWrapperPositionClass={`absolute top-1/2 -translate-y-1/2 left-0 z-10`}
            customWrapperClass={`flex justify-center h-full w-8`}
            customLabelClass="w-full flex items-center justify-end cursor-pointer group"
            customInputClass="cursor-pointer group-hover:ring-2 group-hover:ring-accent-blue-dark"
            type="radio"
            id={`move_to_group_${group_id}`}
            name="move_to_group"
            label={""}
            checked={moveToGroup === group_id}
            onClick={() => {
              setMoveToGroup(group_id);
            }}
            onChange={() => {
              setMoveToGroup(group_id);
            }}
          />
        )}

        {/* Checkbox for selection of groups */}
        {!!hGSelectorMode &&
          modalsState.showReadOnlyViewForHierarchyGroup === null && (
            <div className="flex items-center absolute top-1/2 left-2 -translate-y-1/2 z-10">
              &#8203;
              <input
                type="checkbox"
                className="size-4 rounded border-gray-300"
                checked={selectedHGroups.includes(group_id)}
                onChange={() => {
                  dispatch(setSelectedGroupIsModified(true));
                  setSelectedHGroups(
                    selectedHGroups.includes(group_id)
                      ? selectedHGroups.filter(
                          (selectedGroup) => selectedGroup !== group_id
                        )
                      : [...selectedHGroups, group_id]
                  );
                }}
              />
            </div>
          )}

        {/* Main Group Button here */}
        <button
          type="button"
          className={`absolute w-full h-full group flex items-center gap-3 px-3 z-0 focus:ring-2 focus:ring-inset focus:ring-accent-blue-dark ${
            movingGroup === group_id
              ? "bg-gray-300 grayscale"
              : selected || selectedHGroups.includes(group_id)
                ? " bg-accent-blue-pale"
                : ` hover:bg-accent-blue-paleO66${
                    (index + level) % 2 == 0 ? " bg-slate-100" : ""
                  }`
          }${
            groupBeingMovedCanMoveHere ||
            (!!hGSelectorMode &&
              modalsState.showReadOnlyViewForHierarchyGroup === null)
              ? " pl-8"
              : ""
          }`}
          onClick={
            selected ||
            (selectedHGroups.includes(group_id) &&
              modalsState.showReadOnlyViewForHierarchyGroup === null)
              ? undefined
              : handleGroupClick
          }
          disabled={movingGroup === group_id}
          title={title}
        >
          <div className="flex flex-col items-start flex-1 min-w-0 overflow-hidden">
            <span className="font-semibold text-sm text-heading-gray222">
              {group_id}
            </span>
            <span className="text-xs text-gray-600 text-left whitespace-nowrap overflow-hidden text-ellipsis w-full">
              {description}
            </span>
          </div>
          {movingGroup !== group_id && (
            <img
              src={svgIcons.ChevronRightBlue}
              alt="arrow"
              className={`size-3 flex-shrink-0 ${selected ? "opacity-100" : "opacity-0"} group-hover:opacity-100`}
            />
          )}
          <span className="text-xs text-accent-blue-bright leading-6 underline flex-shrink-0 ml-auto mr-8">
            {t("assets_count", { count: assetsCount })}
          </span>
        </button>

        {/* Three Dots Button */}
        {!movingGroup && !hGSelectorMode && (
          <>
            <button
              type="button"
              className={`absolute size-6 p-[2px] top-1/2 -translate-y-1/2 right-2 rounded-md ${showDropdown ? "bg-white z-modal" : "z-10"}`}
              onClick={handleClickMoreVertIcon}
              disabled={isLoadingDeleteOrgGrp}
            >
              <img
                src={
                  isLoadingDeleteOrgGrp
                    ? svgIcons.LoadingAnimation
                    : svgIcons.MoreVertBlue
                }
                alt="more"
              />
            </button>
            <AppDropdownMenu
              show={showDropdown}
              items={[
                ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >=
                ROLE_ACCESS_LEVELS.WRITE
                  ? [
                      {
                        title: t("menu_edit"),
                        onClick: handleActionEdit,
                      },
                      {
                        title: t("menu_move"),
                        onClick: handleActionMove,
                      },
                      ...(level > 0
                        ? [
                            {
                              title: t("menu_make_parent"),
                              onClick: handleActionMakeParent,
                            },
                          ]
                        : []),
                    ]
                  : getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >=
                      ROLE_ACCESS_LEVELS.READ
                    ? [
                        {
                          title: t("menu_view"),
                          onClick: handleActionEdit,
                        },
                      ]
                    : []),
                ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GROUP) >=
                ROLE_ACCESS_LEVELS.ALL
                  ? [
                      {
                        title: t("menu_delete"),
                        red: true,
                        onClick: () => {
                          setMarkedForDeletion(group_id);
                          handleActionDelete();
                        },
                      },
                    ]
                  : []),
              ]}
              classPositioningTop="top-0"
              classPositioningHorizontal="right-2"
              classMarginTop="mt-10"
              classWidth="w-48"
            />
          </>
        )}
      </div>
      {!movingGroup && (
        <div
          className={`fixed top-0 left-0 w-full h-screen z-overlay bg-modal-overlay${showDropdown ? "" : " hidden"}`}
          onClick={handleClickMoreVertIcon}
        />
      )}
      {markedForDeletion === group_id && (
        <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteConfirm} />
      )}
    </>
  );
};
export default GroupHierarchyListItem;
